import clsx from 'clsx'
import ModalWindow from 'components/molecules/ModalWindow'
import { CONSTS } from 'config/objectConst'
import { useId, useSyncExternalStore } from 'react'
import { useActionCreators, useAppSelector, useSwitcher } from 'shared/hooks'
import { contextActions, typo } from 'shared/styles'

import type { Conversation } from '../../model/types'

import { api } from '../../api'
import { actions } from '../../model/slice'
import { selectors } from '../../utils/selectors'
import css from './QuickActions.module.scss'
import { close, manualSet, openId } from './utils/open'

const actionCN = clsx(contextActions.action, css.action)

export function QuickActions() {
	const id = useSyncExternalStore(openId.subscribe, () => openId.value)
	const conversation = useAppSelector(state =>
		selectors.conversations.selectById(state, id)
	)
	const menuId = useId()

	return (
		<div
			className={clsx(css.menu, contextActions.menu, typo.buttonSmB)}
			id={menuId}
			// @ts-ignore Recheck in React 19
			popover='auto'
			ref={manualSet}
		>
			{conversation && (
				<>
					<OtherOrFocused {...conversation} />
					<Read {...conversation} />
					<Star {...conversation} />
					<Archive {...conversation} />
					<DeleteAction {...conversation} />
				</>
			)}
		</div>
	)
}

function Archive({ entity_id: id }: Conversation) {
	const filter = useAppSelector(selectors.filter)

	const inOnArchive = filter === 'archive'
	const [mutation, { isLoading, originalArgs }] = api.useArchiveMutation()

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={() => {
				mutation({
					id,
					mark: !inOnArchive
				})
				close()
			}}
			type='button'
		>
			{inOnArchive ? 'Restore' : 'Archive'}
		</button>
	)
}

function DeleteAction({ entity_id: conversation }: Conversation) {
	const modal = useSwitcher(false)
	const [mutation, { isLoading, originalArgs }] = api.useDeleteMutation()

	return (
		<>
			<button
				className={actionCN}
				disabled={isLoading && originalArgs === conversation}
				onClick={() => {
					modal.on()
					close()
				}}
				type='button'
			>
				Delete conversation
			</button>

			<ModalWindow
				closeHandler={modal.off}
				closeText='No'
				confirmHandler={async () => {
					try {
						await mutation(conversation).unwrap()
						modal.off()
					} catch (e) {
						console.error(e)
					}
				}}
				confirmText='Yes'
				{...CONSTS.confirmationDialogs['c1.15']}
				open={modal.isOn}
			/>
		</>
	)
}

function OtherOrFocused({ entity_id: conversation }: Conversation) {
	const filter = useAppSelector(selectors.filter),
		shouldShow = useAppSelector(selectors.withTabs)

	const [focusedMutation, focusedOptions] = api.useSetFocusMutation(),
		[otherMutation, otherOptions] = api.useSetOtherMutation()

	if (!shouldShow) {
		return null
	}

	const isOnFocused = filter === 'focused'
	const isOnGoing = focusedOptions.isLoading || otherOptions.isLoading

	const mutation = isOnFocused ? otherMutation : focusedMutation
	const label = `Move to ${isOnFocused ? 'Other' : 'Focused'}`

	return (
		<button
			className={actionCN}
			disabled={
				isOnGoing &&
				(focusedOptions.originalArgs === conversation ||
					otherOptions.originalArgs === conversation)
			}
			onClick={() => {
				mutation(conversation)
				close()
			}}
			type='button'
		>
			{label}
		</button>
	)
}

function Read({ entity_id: id, unread_count: unread }: Conversation) {
	const { updateRead } = useActionCreators(actions)

	const [readMutation, { isLoading, originalArgs }] = api.useMarkReadMutation()

	const isUnread = Boolean(unread)

	const label = `Mark as ${isUnread ? 'read' : 'unread'}`

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={async () => {
				const oldBody = {
					id,
					mark: !isUnread
				}

				const newBody = {
					id,
					mark: isUnread
				}

				updateRead(newBody)
				close()
				try {
					await readMutation(newBody).unwrap()
				} catch (e) {
					updateRead(oldBody)
				}
			}}
			type='button'
		>
			{label}
		</button>
	)
}

function Star({ entity_id: id, is_starred: isStarred }: Conversation) {
	const { updateStar } = useActionCreators(actions)

	const [starMutation, { isLoading, originalArgs }] = api.useStarMutation()

	return (
		<button
			className={actionCN}
			disabled={isLoading && originalArgs?.id === id}
			onClick={async () => {
				const oldBody = {
					id,
					mark: isStarred
				}

				const newBody = {
					id,
					mark: !isStarred
				}

				updateStar(newBody)
				close()
				try {
					await starMutation(newBody).unwrap()
				} catch (e) {
					updateStar(oldBody)
				}
			}}
			type='button'
		>
			{isStarred ? 'Remove star' : 'Star'}
		</button>
	)
}
